import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// Store timestamps of recent API calls to implement basic rate limiting
const requestTimestamps = {
  validateToken: [],
  login: [],
  register: [],
  verifyEmail: [],
  forgotPassword: [],
  resetPassword: [],
};

// Function to check if we should throttle a request
const shouldThrottle = (endpoint, rateLimit = 2, timeWindow = 2000) => {
  const now = Date.now();
  
  // Remove timestamps older than the time window
  requestTimestamps[endpoint] = requestTimestamps[endpoint].filter(
    timestamp => now - timestamp < timeWindow
  );
  
  // If we have fewer requests than the rate limit, allow this one
  if (requestTimestamps[endpoint].length < rateLimit) {
    requestTimestamps[endpoint].push(now);
    return false;
  }
  
  // Otherwise, throttle
  return true;
};

// Clase de error personalizada para errores de la API
class ApiError extends Error {
  constructor(message, data = {}) {
    super(message);
    this.name = 'ApiError';
    this.data = data;
  }
}

const authService = {
  login: async (credentials) => {
    try {
      // Check if we should throttle this request
      if (shouldThrottle('login')) {
        console.warn('Rate limiting login request');
        throw new Error('Por favor, espera un momento antes de intentar nuevamente');
      }
      
      const response = await axios.post(`${API_URL}/api/auth/login`, credentials);
      
      if (response.data && response.data.token) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error) {
      if (error.message === 'Por favor, espera un momento antes de intentar nuevamente') {
        throw new ApiError(error.message, { success: false });
      }
      throw new ApiError(error.response?.data?.message || 'Error en el servidor', 
        error.response?.data || { success: false });
    }
  },
  
  register: async (username, email, password, university) => {
    try {
      // Check if we should throttle this request
      if (shouldThrottle('register')) {
        console.warn('Rate limiting register request');
        throw new Error('Por favor, espera un momento antes de intentar nuevamente');
      }
      
      // Validate email format before sending to server
      const universityDomains = {
        'uach': '@uach.mx',
        'tec': '@tec.mx',
        'ibero': '@correo.uia.mx',
        'anahuac': '@anahuac.mx'
      };
      
      const domain = universityDomains[university];
      if (!domain) {
        throw new Error('Universidad no válida');
      }
      
      if (!email.endsWith(domain)) {
        throw new Error(`El correo debe terminar con ${domain}`);
      }
      
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        username,
        email: email.toLowerCase(),
        password,
        university
      });
      
      // Si hay token, entonces el usuario fue registrado y autenticado inmediatamente
      // (caso cuando la verificación de email está desactivada)
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return { 
          ...response.data,
          requiresVerification: false 
        };
      }
      
      // Si no hay token pero hay éxito, entonces es un registro pendiente de verificación
      if (response.data.success) {
        return { 
          ...response.data,
          requiresVerification: true
        };
      }
      
      return response.data;
    } catch (error) {
      if (error.message === 'Por favor, espera un momento antes de intentar nuevamente') {
        throw new ApiError(error.message, { success: false });
      }
      throw new ApiError(error.response?.data?.message || 'Error en el servidor', 
        error.response?.data || { success: false });
    }
  },
  
  verifyEmail: async (token) => {
    try {
      // Check if we should throttle this request
      if (shouldThrottle('verifyEmail')) {
        console.warn('Rate limiting verify email request');
        throw new Error('Por favor, espera un momento antes de intentar nuevamente');
      }
      
      const response = await axios.get(`${API_URL}/api/auth/verify-success`, {
        params: { token }
      });
      
      if (response.data && response.data.token) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error) {
      if (error.message === 'Por favor, espera un momento antes de intentar nuevamente') {
        throw new ApiError(error.message, { success: false });
      }
      throw new ApiError(error.response?.data?.message || 'Error en la verificación', 
        error.response?.data || { success: false });
    }
  },
  
  forgotPassword: async (email) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email: email.toLowerCase() });
      return response.data;
    } catch (error) {
      if (error.response) {
        // The server responded with a status code outside the 2xx range
        throw error; // Throw the original error to allow components to access error.response
      } else if (error.request) {
        // The request was made but no response was received
        throw new Error('No se recibió respuesta del servidor. Verifica tu conexión.');
      } else {
        // Something happened in setting up the request
        throw new Error('Error al enviar la solicitud.');
      }
    }
  },
  
  resetPassword: async (token, password) => {
    try {
      // Check if we should throttle this request
      if (shouldThrottle('resetPassword')) {
        console.warn('Rate limiting reset password request');
        throw new Error('Por favor, espera un momento antes de intentar nuevamente');
      }
      
      const response = await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        password
      });
      return response.data;
    } catch (error) {
      if (error.message === 'Por favor, espera un momento antes de intentar nuevamente') {
        throw new ApiError(error.message, { success: false });
      }
      throw new ApiError(error.response?.data?.message || 'Error en el servidor', 
        error.response?.data || { success: false });
    }
  },
  
  validateToken: async () => {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      return { success: false };
    }
    
    try {
      // Check if we should throttle this request
      if (shouldThrottle('validateToken', 3, 5000)) {
        console.warn('Rate limiting token validation request');
        // Return cached user data if available instead of failing completely
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          return { success: true, user: JSON.parse(storedUser) };
        }
        throw new Error('Demasiadas solicitudes, por favor intenta más tarde');
      }
      
      const response = await axios.get(`${API_URL}/api/auth/validate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
      }
      
      return response.data;
    } catch (error) {
      if (error.message === 'Demasiadas solicitudes, por favor intenta más tarde') {
        return { success: false, message: error.message };
      }
      
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
      return { success: false };
    }
  },
  
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  }
};

export default authService;